import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Heart, Compass, Star, MessageCircle, Phone } from 'lucide-react';
import { services, testimonials as mockTestimonials } from '../mock';
import { fetchBlogs, fetchTestimonials, fetchSettings, resolveImageUrl } from '../services/api';
import { Fireflies, Moth } from '../components/FairyElements';

const Home = () => {
  const whatsappNumber = '9681424041';
  const whatsappLink = `https://wa.me/91${whatsappNumber}`;

  const featuredServices = services.slice(0, 3);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    fetchBlogs().then(data => setFeaturedBlogs(data.slice(0, 3))).catch(() => setFeaturedBlogs([]));
    fetchTestimonials()
      .then((data) => { if (Array.isArray(data) && data.length) setTestimonials(data.slice(0, 4)); })
      .catch(() => { /* keep mock */ });
    fetchSettings()
      .then((s) => setLogoUrl(s?.logo_url || ''))
      .catch(() => setLogoUrl(''));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1612]">
      {/* Hero Section - The Mystical Forest Entry */}
      <section className="forest-bg min-h-[800px] flex items-center px-4 md:px-8 lg:px-16 py-20 overflow-hidden" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.75}}>
        <Fireflies count={50} />
        <Moth delay={0} top="25%" />
        <Moth delay={8} top="60%" />

        <div className="max-w-6xl mx-auto w-full text-center relative z-10 animate-fadeIn">
          {logoUrl ? (
            <div className="mb-8 flex justify-center animate-fadeIn">
              <img
                src={resolveImageUrl(logoUrl)}
                alt="The Divine 7777"
                className="w-40 md:w-56 lg:w-64 h-auto drop-shadow-[0_0_35px_rgba(217,178,67,0.5)] hover:drop-shadow-[0_0_50px_rgba(217,178,67,0.8)] transition-all duration-700"
                data-testid="hero-logo"
              />
            </div>
          ) : null}

          <div className="inline-block mb-6">
            <span className="vine-divider block w-32 mx-auto" />
            <Badge className="bg-emerald-900/60 border border-amber-500/40 text-amber-300 px-4 py-1 backdrop-blur-md" data-testid="hero-badge">
              <Sparkles className="w-3 h-3 mr-1.5" />
              <span className="tracking-widest text-xs uppercase">Enter the Mystical Garden</span>
            </Badge>
          </div>

          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6 gradient-text text-glow tracking-wider" data-testid="hero-title">
            The Divine7777
          </h1>

          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-12 bg-amber-500/50" />
            <p className="text-lg md:text-xl text-amber-200/90 font-serif-mystical italic">
              Meeta Jain · Tarot, Angel & Oracle Card Reader
            </p>
            <span className="h-px w-12 bg-amber-500/50" />
          </div>

          <p className="text-lg md:text-xl text-amber-100/80 mb-10 max-w-2xl mx-auto leading-relaxed font-serif-mystical">
            Where ancient wisdom blooms among emerald ferns and golden lanterns light the path to spiritual clarity.
          </p>

          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Button
              size="lg"
              className="btn-gold px-10 py-7 text-base tracking-wider uppercase"
              onClick={() => window.open(whatsappLink, '_blank')}
              data-testid="hero-book-btn"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Book Your Reading
            </Button>

            <Button
              size="lg"
              className="btn-mystical px-10 py-7 text-base tracking-wider uppercase"
              asChild
              data-testid="hero-explore-btn"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              <Link to="/services">Explore Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section - Sacred Grove */}
      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/services-grove.png)", '--bg-opacity': 0.3}}>
        <Fireflies count={30} />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
              <Sparkles className="w-3 h-3 mr-1.5" />
              <span className="tracking-widest text-xs uppercase">Sacred Offerings</span>
            </Badge>
            <h2 className="text-5xl md:text-6xl font-bold mb-4 gradient-text">Featured Services</h2>
            <p className="text-lg text-amber-100/70 font-serif-mystical italic">Choose the guidance that calls to your soul</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredServices.map((service) => (
              <Card
                key={service.id}
                className="forest-card border-0 group"
                data-testid={`service-card-${service.id}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-2xl text-amber-200 group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>
                      {service.title}
                    </CardTitle>
                    {service.popular && (
                      <Badge className="bg-amber-600/90 text-emerald-950 border-0 font-semibold">Popular</Badge>
                    )}
                  </div>
                  <CardDescription className="text-amber-100/70">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 pb-4 border-b border-amber-600/20">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {service.discountedPrice ? (
                        <>
                          <span className="text-4xl font-bold text-amber-400">{service.currency}{service.discountedPrice}</span>
                          <span className="text-xl text-amber-100/40 line-through">{service.currency}{service.price}</span>
                        </>
                      ) : service.price ? (
                        <span className="text-4xl font-bold text-amber-400">{service.currency}{service.price}</span>
                      ) : (
                        <span className="text-xl font-semibold text-amber-400">Custom Pricing</span>
                      )}
                    </div>
                    <p className="text-sm text-amber-100/60 mt-1">{service.duration}</p>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-amber-100/80">
                        <Star className="w-4 h-4 mr-2 text-amber-500 fill-amber-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="btn-mystical w-full"
                    onClick={() => window.open(whatsappLink, '_blank')}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button className="btn-mystical px-8" asChild>
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose - Heart of the Forest */}
      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <Fireflies count={25} />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="vine-divider block w-32 mx-auto" />
            <h2 className="text-5xl md:text-6xl font-bold mb-4 gradient-text">Why The Divine7777?</h2>
            <p className="text-lg text-amber-100/70 font-serif-mystical italic">Experience guidance that transforms</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Heart, title: 'Compassionate Guidance', desc: 'Empathetic and non-judgmental readings that honor your unique journey and emotional needs.' },
              { icon: Compass, title: 'Accurate Insights', desc: 'Years of experience combining tarot, angel, and oracle cards for profound spiritual clarity.' },
              { icon: Sparkles, title: 'Holistic Approach', desc: 'Comprehensive guidance covering love, career, relationships, and personal transformation.' }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="forest-card border-0 text-center">
                  <CardHeader>
                    <div className="mx-auto mb-4 w-20 h-20 bg-gradient-to-br from-emerald-800 via-emerald-700 to-amber-700 rounded-full flex items-center justify-center mystical-glow-gold border border-amber-500/40">
                      <Icon className="w-10 h-10 text-amber-300" />
                    </div>
                    <CardTitle className="text-2xl text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-amber-100/70 leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials - Whispers of the Garden */}
      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/blog-library.png)", '--bg-opacity': 0.25}}>
        <Fireflies count={20} />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4 gradient-text">Whispers from Souls Guided</h2>
            <p className="text-lg text-amber-100/70 font-serif-mystical italic mb-6">Read what others say about their experiences</p>
            <Button
              className="btn-mystical"
              onClick={() => window.open('https://share.google/SbynsuBln45TTEfL5', '_blank')}
              data-testid="google-reviews-btn"
            >
              <Star className="mr-2 h-4 w-4 fill-amber-400 text-amber-400" />
              Read All Google Reviews
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="forest-card border-0" data-testid={`home-testimonial-${testimonial.id}`}>
                <CardHeader>
                  <div className="flex gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    {testimonial.image ? (
                      <img
                        src={resolveImageUrl(testimonial.image)}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover border border-amber-500/40"
                      />
                    ) : null}
                    <div>
                      <CardTitle className="text-lg text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>{testimonial.name}</CardTitle>
                      {testimonial.location ? (
                        <CardDescription className="text-amber-100/60">{testimonial.location}</CardDescription>
                      ) : null}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-amber-100/80 italic font-serif-mystical">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button className="btn-mystical px-8" asChild data-testid="home-all-testimonials-btn">
              <Link to="/testimonials">Read More Testimonials</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Blog Preview - Garden of Wisdom */}
      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={20} />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-4 gradient-text">Garden of Wisdom</h2>
            <p className="text-lg text-amber-100/70 font-serif-mystical italic">Spiritual insights blooming from the blog</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {featuredBlogs.map((post) => (
              <Card
                key={post.id}
                className="forest-card border-0 overflow-hidden group cursor-pointer"
                onClick={() => window.location.href = `/blog/${post.id}`}
                data-testid={`home-blog-${post.id}`}
              >
                <div className="h-52 overflow-hidden relative">
                  <img
                    src={resolveImageUrl(post.image)}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/30 to-transparent" />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <Badge className="bg-emerald-800/80 text-amber-300 border border-amber-500/30">{post.category}</Badge>
                    <span className="text-xs text-amber-100/60">{post.readTime}</span>
                  </div>
                  <CardTitle className="text-xl text-amber-200 group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>{post.title}</CardTitle>
                  <CardDescription className="text-amber-100/70">{post.excerpt}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button className="btn-mystical px-8" asChild>
              <Link to="/blog">Read More Articles</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - The Sacred Portal */}
      <section className="forest-bg py-32 px-4 md:px-8 lg:px-16 relative overflow-hidden" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.5}}>
        <Fireflies count={60} />
        <Moth delay={0} top="20%" />
        <Moth delay={5} top="50%" />
        <Moth delay={10} top="75%" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl md:text-7xl font-bold mb-6 gradient-text text-glow">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl mb-10 text-amber-100/80 font-serif-mystical italic max-w-2xl mx-auto">
            Step into the enchanted garden of guidance and discover what the divine has woven for you.
          </p>

          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Button
              size="lg"
              className="btn-gold px-10 py-7 text-base tracking-wider uppercase"
              onClick={() => window.open(whatsappLink, '_blank')}
              data-testid="cta-whatsapp-btn"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              <Phone className="mr-2 h-5 w-5" />
              WhatsApp: {whatsappNumber}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
